import { Button, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { APIRobotInfo, getRobot, account } from '@mygpt/app';

const { ContentWrap } = account;
import { AI_STATUS } from '@/constant';

import {
  SvgConversation,
  SvgConversationActive,
  SvgData,
  SvgDataActive,
  SvgMessage,
  SvgQa,
  SvgQaActive,
  SvgSetting,
  SvgTypeDocument,
  SvgTypeWebsite,
} from './icon';

interface props {
  robot: APIRobotInfo;
}

export default ({ robot }: props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const lgMedia = useMediaQuery('(min-width: 640px)');

  const [isInit, setIsInit] = useState(false);
  const checkTimer = useRef<ReturnType<typeof setTimeout>>();
  const componentUnmount = useRef<boolean>(false);
  const isSourceEmpty = robot.all_file_count === 0 && robot.all_faq_count === 0;

  const isDocumentType = useMemo(() => {
    return robot.ai_model === 'file';
  }, [robot]);

  const navigateAnimat = (url: string) => {
    if (document.startViewTransition) {
      document.startViewTransition(() => navigate(url));
    } else {
      navigate(url);
    }
  };

  const handleChat = () => {
    if (isInit) {
      navigateAnimat(`/create/website/${robot.id}`);
    } else {
      navigateAnimat(`/bot/${robot.id}/chat`);
    }
  };

  const handleEdit = () => {
    navigateAnimat(`/bot/${robot.id}/settings`);
  };

  // const robotTip = useMemo(() => {
  //   switch (robot.ai_model) {
  //     case 'file':
  //       return t('documents');
  //     case 'website':
  //       return t('website');
  //   }
  // }, [robot, t]);

  const checkRobotInit = useCallback(async (id: string) => {
    if (checkTimer.current) clearTimeout(checkTimer.current);
    const res = await getRobot({ aiId: id });
    const { ai_status } = res.data;
    const isInit = ai_status === AI_STATUS.INIT;
    if (isInit && !componentUnmount.current) {
      checkTimer.current = setTimeout(() => {
        checkRobotInit(id);
      }, 5 * 1000);
    } else {
      setIsInit(isInit);
    }
  }, []);

  useEffect(() => {
    if (!robot) return;
    setIsInit(!isDocumentType && robot.ai_status === AI_STATUS.INIT);
  }, [isDocumentType, robot]);

  useEffect(() => {
    if (!isInit) return;
    checkRobotInit(robot.id);

    return () => {
      if (checkTimer.current) {
        clearTimeout(checkTimer.current);
      }
      componentUnmount.current = true;
    };
  }, [checkRobotInit, isInit, robot.id]);

  interface ActionItemsProps {
    icon: JSX.Element;
    activeIcon: JSX.Element;
    label: string;
    to: string;
  }

  const actionItems: Array<ActionItemsProps> = [
    {
      icon: <SvgQa />,
      activeIcon: <SvgQaActive />,
      label: t('menu.bot.data_sources'),
      to: `/bot/${robot.id}/data-source`,
    },
    {
      icon: <SvgData />,
      activeIcon: <SvgDataActive />,
      label: t('menu.bot.dashboard'),
      to: `/bot/${robot.id}/dashboard`,
    },
    {
      icon: <SvgConversation />,
      activeIcon: <SvgConversationActive />,
      label: t('menu.bot.records'),
      to: `/bot/${robot.id}/records`,
    },
  ];

  const ActionItem = (item: ActionItemsProps) => {
    return (
      <Link to={item.to}>
        <span className="group flex gap-2 items-center hover:text-violet-500 transition-all">
          <span className="group-hover:hidden">{item.icon}</span>
          <span className="hidden group-hover:block">{item.activeIcon}</span>
          <span>{item.label}</span>
        </span>
      </Link>
    );
  };

  const Line = () => <span className="border-r border-r-gray-500 h-[10px] w-0 mx-2"></span>;

  const AiTypeText = () => (
    <>
      {robot.ai_type === 'private' && <span>{t('private_text')}</span>}
      {robot.ai_type === 'public' && <span>{t('public_text')}</span>}
    </>
  );

  return (
    <ContentWrap className="p-5 mb-5 hover:scale-[102%] transition-all">
      {/* usage todo */}
      <section className="flex flex-col flex-wrap lg:flex-nowrap lg:flex-row lg:items-center lg:justify-between mb-5">
        <div className="flex items-center">
          <div className="w-[60px] h-[60px] flex justify-center">
            <div className="scale-[76%] -translate-y-1.5">
              {isDocumentType && <SvgTypeDocument />}
              {robot.ai_model === 'website' && <SvgTypeWebsite />}
            </div>
          </div>
          <div className="ml-5 mr-10 grow">
            <p className="mb-2 text-md sm:text-lg text-gray-800">{robot.name}</p>
            <p className="flex items-center text-gray-500 text-sm">
              <span>{dayjs(robot.created_at).format('YYYY-MM-DD')}</span>
              <Line />
              <AiTypeText />
            </p>
          </div>
        </div>
        <div className="flex-shrink-0 mt-3 lg:mt-0">
          {isInit ? (
            <Text c="violet.5">{t('learning')}</Text>
          ) : isSourceEmpty ? (
            <Text c="violet.5">{t('awaiting_sources')}</Text>
          ) : (
            <div>
              <Button leftSection={<SvgSetting />} onClick={handleEdit} disabled={isInit} size={lgMedia ? 'sm' : 'xs'}>
                {t('edit')}
              </Button>
              <Button leftSection={<SvgMessage />} onClick={handleChat} size={lgMedia ? 'sm' : 'xs'} className="ml-2">
                {t('chat')}
              </Button>
            </div>
          )}
        </div>
      </section>
      <section className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 sm:gap-0 bg-gray-50 rounded-lg px-3 py-2 text-gray-700 text-sm">
        {actionItems.map((item, index) => {
          return <ActionItem {...item} key={index}></ActionItem>;
        })}
      </section>
    </ContentWrap>
  );
};
